import React from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { useTracking } from 'react-tracking';
import { ListItem } from '@applatform/design-system';
import { styled, v2Colors } from 'theme';
import Icon from 'components/V1/Icon/Icon';

const StyledLink = styled(Link, {
  display: 'flex',
  alignItems: 'center',
  gap: '$2',
  px: '$3',
  py: '$2',
  mb: '$1',
  border: 0,
  borderRadius: '$md',
  color: v2Colors.ink2,
  fontSize: '$sm',
  lineHeight: '1.2',
  textDecoration: 'none',
  outline: 0,
  WebkitTapHighlightColor: 'rgba(0,0,0,0)',
  transition: 'background-color 200ms, color 200ms',
  '&:hover': {
    backgroundColor: '$violet100',
  },
  '&:focus-visible': {
    boxShadow: '$focus',
  },
  svg: {
    width: '16px',
    height: '16px',
    opacity: 0.8,
  },

  variants: {
    isActive: {
      true: {
        backgroundColor: '$violet100',
        color: v2Colors.purple600,
        fontWeight: 650,
        '&:hover': {
          backgroundColor: '$violet100',
        },
        svg: {
          opacity: 1,
        },
      },
    },
  },
});

interface NavLinkV2Props {
  readonly children: React.ReactNode;
  readonly icon: React.ComponentType;
  readonly onClick?: () => void;
  readonly to: string;
}

/**
 * Allows control for each navlink to be under Feature flags without leaving empty list items.
 * <Feature.On>
 *   <NavLinkV2 ... />
 * </Feature.On>
 */
function NavLinkV2({ children, icon, onClick, to }: NavLinkV2Props) {
  const isActiveRoute = Boolean(useRouteMatch({ path: to, exact: to === '/' }));
  const { trackEvent } = useTracking();

  function handleClick(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    trackEvent({
      name: 'page_navigation',
      value: event.currentTarget.textContent ?? undefined,
    });
    onClick?.();
  }

  return (
    <ListItem>
      <StyledLink
        to={to}
        isActive={isActiveRoute}
        onClick={handleClick}
        aria-current={isActiveRoute ? 'page' : undefined}
      >
        <Icon as={icon} aria-hidden="true" />
        {children}
      </StyledLink>
    </ListItem>
  );
}

export default NavLinkV2;
